import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * React Error Boundary — catches render-phase errors in child components.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary level="widget" name="Health Explorer">
 *     <HealthExplorer />
 *   </ErrorBoundary>
 *
 * Props:
 *   level: "app" | "page" | "widget" (controls the fallback UI size)
 *   name: optional label shown in the error message (e.g., "Health Explorer")
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ErrorBoundary${this.props.name ? `: ${this.props.name}` : ''}]`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const level = this.props.level || 'page';
    const name = this.props.name;

    // App-level: full-screen error
    if (level === 'app') {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background p-4" data-testid="error-boundary-app">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="text-lg font-semibold">Something went wrong</h2>
              <p className="text-sm text-muted-foreground">
                An unexpected error occurred. Please try refreshing the page.
              </p>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <pre className="text-xs text-left bg-muted p-2 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                </pre>
              )}
              <Button onClick={this.handleReload} className="gap-2" data-testid="error-boundary-reload-btn">
                <RefreshCw className="h-4 w-4" /> Reload Page
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Widget-level: compact inline error
    if (level === 'widget') {
      return (
        <div className="border border-destructive/30 bg-destructive/5 rounded-lg p-4 text-center space-y-2" data-testid="error-boundary-widget">
          <div className="flex items-center justify-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">
              {name ? `${name} failed to load` : 'This section failed to load'}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={this.handleReset} className="gap-1" data-testid="error-boundary-retry-btn">
            <RefreshCw className="h-3 w-3" /> Retry
          </Button>
        </div>
      );
    }

    // Page-level (default): medium error card
    return (
      <div className="flex items-center justify-center p-8" data-testid="error-boundary-page">
        <Card className="max-w-sm w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
            <h3 className="font-semibold">
              {name ? `${name} encountered an error` : 'Something went wrong'}
            </h3>
            <p className="text-sm text-muted-foreground">
              This page encountered an error. You can try again or navigate to another page.
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm" onClick={this.handleReset} className="gap-1" data-testid="error-boundary-retry-btn">
                <RefreshCw className="h-3 w-3" /> Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
